import { Button } from '@/components/shadcn/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/shadcn/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/ui/form';
import { Input } from '@/components/shadcn/ui/input';
import { Textarea } from '@/components/shadcn/ui/textarea';
import { toast } from '@/components/shadcn/ui/use-toast';
import CustomInputPercentageMask from '@/utils/customInputPercentageMask';
import CustomInputPhoneMask from '@/utils/customInputPhoneMask';
import { zodResolver } from '@hookform/resolvers/zod';
import { PlusIcon, BoxIcon, EditIcon } from 'lucide-react';
import { ChangeEvent, FormEvent, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import React from 'react';
import { useSellersContext } from '@/context/SellerContext';

const schema = z.object({
  id: z.number().optional(),
  name: z.string().min(1, 'Nome é obrigatório'),
  email: z.string().email('E-mail inválido'),
  phone: z.string(),
  commission: z.string().min(1, 'Comissão é obrigatória'),
});

type SellerFormProps = {
    onSellerSubmit: (index:number | undefined, data:any) => void;
}
// eslint-disable-next-line
const SellerForm:React.FC<SellerFormProps> = ({onSellerSubmit}) => {
  const form = useForm({
    resolver: zodResolver(schema),
  });

  const { open, setOpen, isEdit, setEdit, indexEdit, data2Edit, setData2Edit } = useSellersContext();

  useEffect(() => {
    if (data2Edit) {
      form.reset({
        id: data2Edit.id,
        name: data2Edit.name,
        email: data2Edit.email,
        phone: data2Edit.phone,
        commission: data2Edit.commission,
      });
    }
  }, [data2Edit, form]);

  function onSubmit(data: z.infer<typeof schema>) {
    
    toast({
      title: 'Erro! Valores indefinidos não são permitidos:',
      description: `Nome: ${data.name === undefined ? data.name : 'Indefinido'}`,
    });
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
  
    const data = form.getValues();
    // Validação da comissão
    if (data.commission >= 20) {
      toast({
        variant: 'destructive',
        title: 'Comissão inválida',
        description: 'O percentual de comissão deve ser menor que 20%.',
      });
      return;
    }
  
    if (isEdit) {
      onSellerSubmit(indexEdit, data);
    } else {
      onSellerSubmit(undefined, data);
    }
  
    setOpen(false);
  };

  const percentageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    e.target.value = CustomInputPercentageMask.porcentagem(value);
  };

  const phoneInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    e.target.value = CustomInputPhoneMask.format(value);
  };

  const isOpen = false;

  return (
    <Dialog open = {open} onOpenChange={()=>{
      setOpen(!open);
      setEdit(false);
      setData2Edit(undefined);
    }}>
      <DialogTrigger >
        <div className=''>
          <Button variant='ghost' className='gap-1 mt-2 bg-indigo-50 hover:bg-indigo-100' > 
            <PlusIcon size={12}/> 
            Adicionar Vendedor
          </Button>
        </div>
      </DialogTrigger> 
      <DialogContent className='w-4/5 h-[70%]'> 
        <DialogHeader>
          <DialogTitle>
            <div className='flex items-center gap-2'>
              { isEdit ? <EditIcon/> : <PlusIcon/> } 
              { isEdit ? 'Editar Vendedor' : 'Novo Vendedor' }
            </div>
          </DialogTitle>
        </DialogHeader>
            
        {/* Começo do Formulário */}
        <Form {...form}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className='grid gap-4 pt-2'>
      
              {/* id */}
              <FormField
                control={form.control}
                name="id"
                render={({ field }) => (
                  <input type="hidden" onChange={(e) => {
                    field.onChange(e);
                  }} defaultValue={isEdit ? data2Edit.id : NaN} />
                )}
              />
      
              {/* nome */}
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome do Vendedor</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder={isEdit ? data2Edit.name : 'Digite o nome do vendedor'}
                        defaultValue={isEdit ? data2Edit.name : ''}
                        onChange={(e) => {
                          field.onChange(e);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
      
              {/* email */}
              <FormField
                control={form.control}
                name='email'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>E-mail</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={'Digite o email do Vendedor'}
                        defaultValue={isEdit ? data2Edit.email : ''}
                        onChange={(e) => {
                          field.onChange(e);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
      
              {/* telefone */}
              <FormField
                control={form.control}
                name='phone'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Telefone</FormLabel>
                    <FormControl>
                      <Input placeholder={'Digite o telefone do Vendedor'}
                        defaultValue={isEdit ? CustomInputPhoneMask.format(data2Edit.phone) : ''}
                        onChange={(e) => {
                          phoneInputChange(e);
                          field.onChange(e);
                        }} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* comissao */}
              <FormField
                control={form.control}
                name='commission'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Percentual de comissão (%)</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder={'Digite a comissão para este Vendedor'}
                        defaultValue={isEdit ? data2Edit.commission : ''}
                        onChange={(e) => {
                          percentageInputChange(e);
                          field.onChange(e);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <DialogFooter>
              <Button type='submit' className='bg-indigo-700' onClick={()=>{
                const data = form.getValues();
                setOpen(!open);
              }}>{isEdit ? 'Editar Vendedor' : 'Cadastrar Vendedor'}</Button>
            </DialogFooter>
          </form>
        </Form> 
      </DialogContent> 
    </Dialog>
  );
};

export default SellerForm;
